import React from 'react';
import { Satellite, LineChart, Database, Shield } from 'lucide-react';

const MRVSection = () => {
  const features = [
    {
      icon: Satellite,
      title: 'Satellite Monitoring',
      description: 'Real-time satellite imagery tracks your farm\'s carbon sequestration and soil health improvements with precision.',
      image: 'https://images.unsplash.com/photo-1581922813291-c6d23508c2c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxzYXRlbGxpdGUlMjBhZ3JpY3VsdHVyZXxlbnwwfHx8fDE3NjIzNDExMjl8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: LineChart,
      title: 'Advanced Analytics',
      description: 'Comprehensive dashboards provide insights into biochar production, CO₂ offset, and financial returns.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxkYXNoYm9hcmQlMjBtb25pdG9yaW5nfGVufDB8fHx8MTc2MjMzNzc4OHww&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Database,
      title: 'Verified Data Collection',
      description: 'Automated data collection ensures accuracy and transparency for carbon credit certification and compliance.',
      image: 'https://images.unsplash.com/photo-1720200793798-947f201e2028?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxzYXRlbGxpdGUlMjBhZ3JpY3VsdHVyZXxlbnwwfHx8fDE3NjIzNDExMjl8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Shield,
      title: 'Blockchain Secured',
      description: 'All data is cryptographically secured on blockchain, ensuring immutable records for carbon credit trading.',
      image: 'https://images.unsplash.com/photo-1639322537231-2f206e06af84?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxibG9ja2NoYWluJTIwbmV0d29ya3xlbnwwfHx8fDE3NjIzNDExMzV8MA&ixlib=rb-4.1.0&q=85'
    }
  ];

  return (
    <section id="mrv" className="pt-0 pb-0 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-16 pt-20">
          <h2 className="text-4xl font-heading font-bold text-neo-black mb-4">
            Digital MRV (Monitoring, Reporting & Verification)
          </h2>
          <p className="text-xl text-neo-gray max-w-3xl mx-auto font-body mb-8">
            Cutting-edge technology ensures transparent and verified carbon credit generation
          </p>
          
          <div className="max-w-4xl mx-auto mb-8">
            <h3 className="text-2xl font-heading font-bold text-neo-black mb-4 text-center">Align With Global Standards</h3>
            <p className="text-center text-lg text-neo-gray font-body mb-6">
              This section highlights global verification and standards, not just Indian compliance.
            </p>
            <div className="flex flex-col items-center max-w-2xl mx-auto space-y-4">
              <div className="flex items-start gap-4 w-full">
                <div className="w-8 h-8 bg-neo-emerald/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-neo-emerald text-lg font-bold">✓</span>
                </div>
                <p className="text-lg text-neo-gray font-body leading-relaxed pt-1 flex-1">
                  Aligned with Verra VM0044, IPCC guidelines & Gold Standard MRV frameworks.
                </p>
              </div>
              <div className="flex items-start gap-4 w-full">
                <div className="w-8 h-8 bg-neo-emerald/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-neo-emerald text-lg font-bold">✓</span>
                </div>
                <p className="text-lg text-neo-gray font-body leading-relaxed pt-1 flex-1">
                  Satellite-based MRV compatible with international carbon registry requirements.
                </p>
              </div>
              <div className="flex items-start gap-4 w-full">
                <div className="w-8 h-8 bg-neo-emerald/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-neo-emerald text-lg font-bold">✓</span>
                </div>
                <p className="text-lg text-neo-gray font-body leading-relaxed pt-1 flex-1">
                  Blockchain-backed verification for global carbon credit issuance.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div 
                key={index} 
                className="bg-gray-50 border border-neo-emerald/20 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-neo-emerald/20 hover:border-neo-emerald/40 transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-neo-black/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 bg-neo-emerald/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-neo-emerald/30">
                      <Icon className="w-6 h-6 text-neo-emerald" />
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-heading font-bold text-neo-black mb-2">{feature.title}</h3>
                  <p className="text-neo-gray font-body">{feature.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 bg-gray-50 border border-neo-emerald/20 rounded-2xl p-8 shadow-lg">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-heading font-bold text-neo-emerald mb-2">99.9%</div>
              <p className="text-neo-gray font-body">Data Accuracy</p>
            </div>
            <div>
              <div className="text-4xl font-heading font-bold text-neo-emerald mb-2">24/7</div>
              <p className="text-neo-gray font-body">Real-time Monitoring</p>
            </div>
            <div>
              <div className="text-4xl font-heading font-bold text-neo-emerald mb-2">ISO</div>
              <p className="text-neo-gray font-body">Certified Standards</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MRVSection;
